import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  getColumnsForVersion,
  type ExpandedColumnDef,
} from '../components/workspace/columnRegistry';

const STORAGE_KEY = 'forecast-v3-expanded-columns';

interface ExpandedColumnsPreference {
  visibleColumns: string[];
}

function loadPreference(storageKey: string, columns: ExpandedColumnDef[]): ExpandedColumnsPreference | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Validate that all keys are still valid column keys
    const validKeys = new Set(columns.map(c => c.key));
    const visibleColumns = (parsed.visibleColumns as string[]).filter(k => validKeys.has(k));
    return visibleColumns.length > 0 ? { visibleColumns } : null;
  } catch {
    return null;
  }
}

function savePreference(pref: ExpandedColumnsPreference, storageKey: string) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(pref));
  } catch {
    // localStorage may be unavailable
  }
}

export function useExpandedColumnConfig(isBudget = false) {
  const storageKey = isBudget ? `${STORAGE_KEY}-budget` : STORAGE_KEY;
  const allColumns = useMemo(() => getColumnsForVersion(isBudget), [isBudget]);
  const defaults = useMemo(() => allColumns.filter(c => c.defaultVisible).map(c => c.key), [allColumns]);
  const [visibleKeys, setVisibleKeys] = useState<string[]>(() => {
    const saved = loadPreference(storageKey, allColumns);
    return saved?.visibleColumns ?? defaults;
  });

  useEffect(() => {
    setVisibleKeys(loadPreference(storageKey, allColumns)?.visibleColumns ?? defaults);
  }, [storageKey, allColumns, defaults]);
  const activeColumns = useMemo(() => visibleKeys.map(key => allColumns.find(c => c.key === key))
    .filter((c): c is ExpandedColumnDef => !!c), [visibleKeys, allColumns]);
  const activeColumnCount = activeColumns.length;



  const updateKeys = useCallback((keys: string[]) => {
    setVisibleKeys(keys);
    savePreference({ visibleColumns: keys }, storageKey);
  }, [storageKey, defaults]);

  const toggleColumn = useCallback((key: string) => {
    setVisibleKeys(prev => {
      const next = prev.includes(key)
        ? prev.filter(k => k !== key)
        : [...prev, key];
      savePreference({ visibleColumns: next }, storageKey);
      return next;
    });
  }, [storageKey, defaults]);

  const moveColumn = useCallback((key: string, direction: 'up' | 'down') => {
    setVisibleKeys(prev => {
      const idx = prev.indexOf(key);
      if (idx === -1) return prev;
      const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
      savePreference({ visibleColumns: next }, storageKey);
      return next;
    });
  }, [storageKey, defaults]);

  const resetToDefaults = useCallback(() => {
    setVisibleKeys(defaults);
    savePreference({ visibleColumns: defaults }, storageKey);
  }, [storageKey, defaults]);

  const isColumnVisible = useCallback((key: string) => visibleKeys.includes(key), [visibleKeys]);

  return {
    activeColumns,
    activeColumnCount,
    allColumns,
    visibleKeys,
    toggleColumn,
    moveColumn,
    resetToDefaults,
    isColumnVisible,
    updateKeys,
  };
}
